import { activeTenant } from '../../lib/tenant'

export type PortalPrintColumn = {
  label: string
  align?: 'left' | 'center' | 'right'
}

export type PortalPrintDocument = {
  title: string
  eyebrow: string
  reference: string
  issuedAt?: string
  meta?: Array<{ label: string; value: unknown }>
  columns?: PortalPrintColumn[]
  rows?: Array<Array<unknown>>
  totals?: Array<{ label: string; value: unknown }>
  notes?: string[]
  signatures?: string[]
  autoPrint?: boolean
}

const escapeHtml = (value: unknown) =>
  String(value ?? '—').replace(/[&<>"']/g, char => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  })[char]!)

export function openPortalPrint(documentData: PortalPrintDocument): boolean {
  const popup = window.open('', '_blank', 'width=980,height=820')
  if (!popup) return false

  const site = (window as typeof window & {
    __webappContent?: {
      identity?: { legalName?: string; displayName?: string; shortLocation?: string }
      brand?: { logoUrl?: string }
    }
  }).__webappContent
  const logo = site?.brand?.logoUrl || activeTenant.logoUrl
  const legalName = site?.identity?.legalName || site?.identity?.displayName || activeTenant.legalName
  const shortLocation = site?.identity?.shortLocation || 'República Dominicana'
  const issuedAt = documentData.issuedAt || new Intl.DateTimeFormat('es-DO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date())
  const meta = (documentData.meta || []).map(item =>
    `<div class="meta-item"><span>${escapeHtml(item.label)}</span><strong>${escapeHtml(item.value)}</strong></div>`
  ).join('')
  const columns = (documentData.columns || []).map(column =>
    `<th class="align-${column.align || 'left'}">${escapeHtml(column.label)}</th>`
  ).join('')
  const rows = (documentData.rows || []).map(row =>
    `<tr>${row.map((cell, index) => `<td class="align-${documentData.columns?.[index]?.align || 'left'}">${escapeHtml(cell)}</td>`).join('')}</tr>`
  ).join('')
  const totals = (documentData.totals || []).map(item =>
    `<div><span>${escapeHtml(item.label)}</span><strong>${escapeHtml(item.value)}</strong></div>`
  ).join('')
  const notes = (documentData.notes || []).map(note => `<li>${escapeHtml(note)}</li>`).join('')
  const signatures = (documentData.signatures || []).map(label =>
    `<div class="signature"><span></span><strong>${escapeHtml(label)}</strong></div>`
  ).join('')

  popup.document.open()
  popup.document.write(`<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${escapeHtml(documentData.title)}</title>
  <style>
    :root{--navy:#0b2440;--blue:#0c66e4;--gold:#b88a2d;--ink:#172b4d;--muted:#626f86;--line:#dfe1e6;--soft:#f7f8f9}
    *{box-sizing:border-box}
    @page{size:Letter portrait;margin:.48in}
    body{margin:0;background:#eef1f5;color:var(--ink);font-family:Intro,Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;font-size:12px;line-height:1.45;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .toolbar{position:sticky;top:0;z-index:3;display:flex;justify-content:flex-end;gap:10px;padding:12px 18px;background:rgba(11,36,64,.96);box-shadow:0 2px 10px rgba(9,30,66,.18)}
    .toolbar button{min-height:40px;border:1px solid rgba(255,255,255,.28);border-radius:7px;padding:0 16px;background:#fff;color:var(--navy);font:inherit;font-size:13px;font-weight:700;cursor:pointer}
    .toolbar .primary{border-color:var(--blue);background:var(--blue);color:#fff}
    .sheet{width:min(8.5in,calc(100% - 28px));min-height:11in;margin:24px auto;padding:.58in .62in .48in;background:#fff;box-shadow:0 8px 30px rgba(9,30,66,.14)}
    .brand{display:flex;align-items:center;justify-content:space-between;gap:24px;padding-bottom:17px;border-bottom:3px solid var(--navy)}
    .brand-main{display:flex;align-items:center;gap:13px}
    .brand img{width:48px;height:48px;object-fit:contain}
    .brand strong,.brand span{display:block}
    .brand strong{color:var(--navy);font-size:14px;letter-spacing:.01em}
    .brand span{margin-top:2px;color:var(--muted);font-size:9px;text-transform:uppercase;letter-spacing:.08em}
    .reference{text-align:right}
    .reference strong{font-size:12px}
    .reference span{font-size:9px}
    .heading{display:grid;grid-template-columns:1fr auto;align-items:end;gap:18px;margin:28px 0 20px}
    .eyebrow{margin:0 0 5px;color:var(--gold);font-size:9px;font-weight:800;letter-spacing:.11em;text-transform:uppercase}
    h1{margin:0;color:var(--navy);font-size:25px;line-height:1.1;letter-spacing:-.025em}
    .status{border:1px solid #b3d4ff;border-radius:99px;padding:5px 10px;background:#e9f2ff;color:#0055cc;font-size:9px;font-weight:800;text-transform:uppercase}
    .meta{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-bottom:22px}
    .meta-item{min-height:64px;padding:11px 12px;border:1px solid var(--line);border-radius:8px;background:var(--soft)}
    .meta-item span,.meta-item strong{display:block}
    .meta-item span{margin-bottom:5px;color:var(--muted);font-size:8px;font-weight:700;letter-spacing:.06em;text-transform:uppercase}
    .meta-item strong{color:var(--navy);font-size:11px;overflow-wrap:anywhere}
    table{width:100%;border-collapse:collapse;margin-top:8px;table-layout:auto}
    th{padding:9px 8px;border-bottom:2px solid var(--navy);background:#f1f2f4;color:var(--navy);font-size:8px;letter-spacing:.055em;text-transform:uppercase}
    td{padding:10px 8px;border-bottom:1px solid var(--line);font-size:10px;vertical-align:top;overflow-wrap:anywhere}
    tbody tr:nth-child(even){background:#fafbfc}
    .align-left{text-align:left}.align-center{text-align:center}.align-right{text-align:right}
    .totals{display:grid;justify-content:end;gap:7px;margin:20px 0 0}
    .totals div{display:grid;grid-template-columns:minmax(150px,1fr) auto;gap:28px;min-width:300px;padding:8px 0;border-bottom:1px solid var(--line)}
    .totals span{color:var(--muted);font-size:10px}
    .totals strong{color:var(--navy);font-size:12px;text-align:right}
    .notes{margin:22px 0 0;padding:14px 18px 14px 30px;border-left:3px solid var(--gold);background:#fff8e6;color:#44546f;font-size:9px}
    .notes li+li{margin-top:4px}
    .signatures{display:grid;grid-template-columns:repeat(2,1fr);gap:48px;margin-top:54px}
    .signature span{display:block;border-top:1px solid #7a869a}
    .signature strong{display:block;margin-top:6px;color:var(--muted);font-size:9px;text-align:center}
    footer{display:flex;justify-content:space-between;gap:16px;margin-top:38px;padding-top:11px;border-top:1px solid var(--line);color:var(--muted);font-size:8px}
    @media(max-width:700px){.sheet{width:100%;min-height:0;margin:0;padding:26px 18px;box-shadow:none}.meta{grid-template-columns:1fr 1fr}.heading{grid-template-columns:1fr}.toolbar{position:static}.brand{align-items:flex-start}.brand img{width:40px;height:40px}h1{font-size:22px}.table-wrap{overflow-x:auto}.signatures{gap:24px}}
    @media print{body{background:#fff}.toolbar{display:none!important}.sheet{width:auto;min-height:auto;margin:0;padding:0;box-shadow:none}.meta-item,table,tr,td,th,.totals,.notes,.signatures{break-inside:avoid}footer{position:relative}.status{background:#e9f2ff!important}}
  </style>
</head>
<body>
  <div class="toolbar">
    <button type="button" id="closeDocument">Cerrar</button>
    <button type="button" class="primary" id="printDocument">Imprimir / guardar PDF</button>
  </div>
  <main class="sheet">
    <header class="brand">
      <div class="brand-main"><img src="${escapeHtml(logo)}" alt="${escapeHtml(legalName)}"><div><strong>${escapeHtml(legalName)}</strong><span>Documento emitido desde el portal web</span></div></div>
      <div class="reference"><strong>${escapeHtml(documentData.reference)}</strong><span>Emitido: ${escapeHtml(issuedAt)}</span></div>
    </header>
    <section class="heading"><div><p class="eyebrow">${escapeHtml(documentData.eyebrow)}</p><h1>${escapeHtml(documentData.title)}</h1></div><span class="status">Documento informativo</span></section>
    ${meta ? `<section class="meta">${meta}</section>` : ''}
    ${columns && rows ? `<div class="table-wrap"><table><thead><tr>${columns}</tr></thead><tbody>${rows || `<tr><td colspan="${documentData.columns?.length || 1}">Sin movimientos para mostrar.</td></tr>`}</tbody></table></div>` : ''}
    ${totals ? `<section class="totals">${totals}</section>` : ''}
    ${notes ? `<ul class="notes">${notes}</ul>` : ''}
    ${signatures ? `<section class="signatures">${signatures}</section>` : ''}
    <footer><span>${escapeHtml(legalName)} · ${escapeHtml(shortLocation)}</span><span>Información actualizada desde el portal</span></footer>
  </main>
</body>
</html>`)
  popup.document.close()

  const waitUntilReady = async () => {
    try {
      await popup.document.fonts?.ready
      const images = Array.from(popup.document.images)
      await Promise.all(images.map(image => image.complete
        ? Promise.resolve()
        : new Promise<void>(resolve => {
            image.addEventListener('load', () => resolve(), { once: true })
            image.addEventListener('error', () => resolve(), { once: true })
          })))
    } catch {
      // The document remains printable with system fonts if a resource fails.
    }
  }

  popup.document.getElementById('closeDocument')?.addEventListener('click', () => popup.close())
  popup.document.getElementById('printDocument')?.addEventListener('click', () => {
    void waitUntilReady().then(() => {
      popup.focus()
      popup.print()
    })
  })

  if (documentData.autoPrint !== false) {
    void waitUntilReady().then(() => window.setTimeout(() => {
      popup.focus()
      popup.print()
    }, 180))
  }
  return true
}
